import { newslettersPage } from "common/lib/router"
import { useCsrfToken } from "common/hooks/data/useCsrfToken"
import { useCallback, useState } from "react"
import { trackNewsletterSubscribed as trackNewsletterSubscribedInHouse } from "common/lib/tcEventCollector"
import { useViewer } from "common/hooks/data/useViewer"
import {
  identifyBrazeUser,
  setBrazeTrackingCookie,
} from "@theconversation/shared-libraries/braze-tc"
import { getRuntimeConfig } from "common/lib/runtimeConfig"
import { getUtmAttribution } from "common/lib/utm/getUtmAttribution"

export interface SubscribeEmailParams {
  regionCode: string
  email: string
  recaptchaToken?: string
  newsletterListIds: Set<string>
  action: "subscribe" | "unsubscribe"
}

const DEFAULT_LOCATION = "newsletters-subscription-page-redesign"

interface SuccessResponseFormat {
  success: boolean
}
export interface ErrorResponseFormat {
  errors: string[]
}

/**
 * `toggleSubscription` rejects with an `ErrorResponseFormat` when the API returns a
 * handled error, but that is not the only way it can reject: a network-level `fetch`
 * failure rejects with a `TypeError`, and a non-JSON error body (e.g. an HTML 502 from
 * the CDN) makes `response.json()` throw. Neither has an `errors` property.
 *
 * Narrow with this rather than asserting `error as ErrorResponseFormat` — a `catch`
 * binding is `unknown`, so the cast compiles but throws at runtime on `errors[0]`.
 */
export const isErrorResponseFormat = (error: unknown): error is ErrorResponseFormat =>
  typeof error === "object" &&
  error !== null &&
  Array.isArray((error as ErrorResponseFormat).errors)

/**
 * Hook to toggle an email subscription to newsletters given a region code, email and newsletter list ids.
 */
export const useToggleSubscription = () => {
  const { token: CSRFToken } = useCsrfToken()
  const { isSignedIn } = useViewer()
  const [isLoading, setIsLoading] = useState(false)

  const toggleSubscription = useCallback(
    async ({
      regionCode,
      email,
      recaptchaToken,
      newsletterListIds,
      action,
    }: SubscribeEmailParams) => {
      setIsLoading(true)

      const method = action === "subscribe" ? "POST" : "DELETE"
      const path = newslettersPage.subscriptionsPath({ regionCode, format: "json" })
      let response: Response
      try {
        response = await fetch(path, {
          method,
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": CSRFToken,
          },
          body: JSON.stringify({
            subscribe: {
              email,
              location: DEFAULT_LOCATION,
              recaptcha_token: recaptchaToken,
              newsletter_list_ids: Array.from(newsletterListIds),
            },
          }),
        })
      } finally {
        setIsLoading(false)
      }

      if (!response.ok) {
        const errorResponse: ErrorResponseFormat = await response.json()
        return Promise.reject(errorResponse)
      }

      const successResponse: SuccessResponseFormat = await response.json()

      if (action === "subscribe") {
        // Identify the current user with Braze
        identifyBrazeUser(email).catch(() => {
          // adblockers or running in Cypress can cause this to fail, so just ignore them
          // and let our users continue to subscribe to newsletters.
        })

        // Indicate this user should continue to be tracked by Braze in the future
        setBrazeTrackingCookie(getRuntimeConfig().TC_COOKIE_DOMAIN)

        const utmAttributions = getUtmAttribution()

        // Track successful subscriptions for each newsletter ID
        newsletterListIds.forEach((newsletterId) => {
          trackNewsletterSubscribedInHouse(newsletterId, isSignedIn, utmAttributions) // in-house analytics
        })
      }

      return successResponse
    },
    [CSRFToken, isSignedIn],
  )

  return { isLoading, toggleSubscription }
}
